import React from 'react';

import Wahanda from 'common/wahanda';
import { DialogFooter } from 'components/common/DialogFooter';
import Dialog from 'components/common/react-dialog';
import { track } from 'src/tracking/trackingService';

import { ClientsAnalytics } from '../../tracking';
import type { SelectedFile } from '../types';
import type { ClientImportEntryContext } from '../types/entryContext';
import style from './ClientsImportInit.scss';
import { DownloadTemplate } from './DownloadTemplate/container';
import { FileUpload } from './FileUpload';
import { FillInDetails } from './FillInDetails';
import { Step } from './Step';

const lang = Wahanda.lang.clientsImport;

export interface Props {
  onClose: () => void;
  isFileUploading: boolean;
  selectedFile: SelectedFile;
  entryContext?: ClientImportEntryContext | null;
  actions: {
    clientsFileUploadRequest: (payload: SelectedFile) => void;
    clientsImportReset: () => void;
  };
}

export class ClientsImportInit extends React.PureComponent<Props> {
  private uploadInitiated = false;

  public componentDidMount() {
    const { source, autoOpened, clientListEmpty } = this.props.entryContext!;
    track('clients.management.client_list_upload.viewed', {
      source,
      auto_opened: autoOpened,
      client_list_empty: clientListEmpty,
    });
  }

  public render() {
    return (
      <Dialog
        dataTest="clients-import-modal"
        noContentPadding
        onClose={this.onClose}
        restrictHeight
        title={lang.addMultipleClients.header}
        width={600}
      >
        {this.renderContent()}
        {this.renderFooter()}
      </Dialog>
    );
  }

  private renderContent = () => (
    <div className={style.container}>
      <Step count={1}>
        <DownloadTemplate />
      </Step>
      <Step count={2}>
        <FillInDetails />
      </Step>
      <Step count={3} withLine={false}>
        <FileUpload />
      </Step>
    </div>
  );

  private renderFooter = () => (
    <DialogFooter
      buttons={[
        {
          type: 'secondary',
          title: Wahanda.lang.shared.close,
          onClick: this.onClose,
        },
        {
          title: lang.addMultipleClients.addClients,
          onClick: this.onAddClientsClick,
          isLoading: this.props.isFileUploading,
          disabled: this.props.isFileUploading || !this.isFileSelected(),
        },
      ]}
    />
  );

  private onClose = () => {
    if (!this.uploadInitiated) {
      track('clients.management.client_list_upload.dismissed', {
        source: this.props.entryContext!.source,
        step: 'init',
      });
    }
    ClientsAnalytics.trackClientsImportCloseClick();
    this.props.onClose();
    this.props.actions.clientsImportReset();
  };

  private isFileSelected = () =>
    this.props.selectedFile.file !== null && this.props.selectedFile.fileName !== null;

  private onAddClientsClick = () => {
    this.uploadInitiated = true;
    track('clients.management.client_list_upload.initiated', {
      source: this.props.entryContext!.source,
    });
    ClientsAnalytics.trackClientsImportAddClientsClick();
    this.props.actions.clientsFileUploadRequest(this.props.selectedFile);
  };
}
